// Awards, milestones, and the trophy room on a simulated league
// (AWARDS.md): the pure engine from @fsp/awards hosted by the sim. The sim
// builds the season's facts from what it has (finals, the live rows, the
// transaction log, the bracket, the remembered past), mints weekly awards
// when a period's last game ends and season awards when the championship
// does, posts the cards to the channel, and serves the views.

import type {
  MilestoneView,
  AwardCardView,
  AwardsView,
  ChatCard,
  SeasonWrapView,
  TeamRef,
  TrophyRoomView,
  MilestoneUnlockView,
  WeeklyHat,
  YearbookView,
} from '@fsp/domain';
import { WEEKLY_HATS, drawnHat, weeklyHatSaid } from '@fsp/domain';
import { leagueYearbook, seasonWrap, yearbookWith } from '@fsp/recap';
import {
  DEFAULT_CATALOG,
  earnedInLabel,
  factsThrough,
  milestoneProgress,
  careerLine,
  tierRank,
  formatValue,
  mint,
  periodAwards,
  seasonAwards,
  trophyRoom,
  weekHat,
  type AwardResult,
  type AwardRules,
  type MatchupFact,
  type Minted,
  type PeriodFacts,
  type SeasonFacts,
  type TeamPeriodFact,
  type Tier,
  type Tone,
  type TransactionFact,
} from '@fsp/awards';
import { football } from '@fsp/sports-football';
import { systemMessage } from './chat';
import { GAME_SECONDS } from './games';
import type { SimState, SimTeam } from './league';
import { bracketView } from './playoffs';
import { startedPoints } from './past-weeks';
import { recapMessage } from './recap';
import { hashSeed, rng } from './rng';
import {
  leagueRef,
  lineupRows,
  periodRef,
  periodStatus,
  sideScore,
  standingsView,
  teamRef,
} from './views';

const H = 3600 * 1000;
const WEEK = 7 * 24 * H;
const MIN = 60_000;
const slotDefs = new Map(football.slots.map((s) => [s.id, s]));

export interface Unlock {
  id: string;
  teamId: string;
  milestoneId: string;
  name: string;
  icon: string;
  tone: Tone;
  tier: Tier;
  line: string;
  at: number;
  /** The week whose end earned it; null for a career tier the seasons before reached. */
  period: number | null;
}

export interface AwardState {
  minted: Minted[];
  /** Things already done: 'weekly:3', 'season'. */
  flags: Set<string>;
  /** `${teamId}:${milestoneId}:${tier}` already earned. */
  unlocked: Set<string>;
  /** Every unlock in order, for the Season tab's recent list. */
  unlocks: Unlock[];
}

export function emptyAwardState(): AwardState {
  return { minted: [], flags: new Set(), unlocked: new Set(), unlocks: [] };
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function rulesOf(state: SimState): AwardRules {
  const a = state.settings.fun.leagueAwards;
  return {
    perPeriod: a.perPeriod,
    perSeason: a.perSeason,
    milestones: state.settings.fun.achievements.enabled,
    disabled: a.disabled,
    names: a.names,
  };
}

function seated(state: SimState): SimTeam[] {
  return state.teams.filter((t) => t.ownerName !== '');
}

/** When a period's last game ends: the same slot every week, offset from the current period's. */
export function periodFinalAt(state: SimState, period: number): number {
  const ends = state.proGames.map((g) => g.startAt + GAME_SECONDS * 1000);
  return Math.max(...ends) + (period - state.currentPeriod) * WEEK;
}

// ── Facts ─────────────────────────────────────────────────────────────────

/** A team's regular-season record from the finals through the period before `period`. */
function recordBefore(
  state: SimState,
  teamId: string,
  period: number,
): { wins: number; losses: number } {
  let wins = 0;
  let losses = 0;
  const last = Math.min(period - 1, state.regularSeasonEnd, state.currentPeriod - 1);
  for (let p = 1; p <= last; p++) {
    const m = state.schedule.find(
      (x) => x.period === p && (x.home === teamId || x.away === teamId),
    );
    if (!m) continue;
    const mine = state.finals.get(`${p}:${teamId}`);
    const theirs = state.finals.get(`${p}:${m.home === teamId ? m.away : m.home}`);
    if (mine === undefined || theirs === undefined) continue;
    if (mine > theirs) wins++;
    else if (mine < theirs) losses++;
  }
  return { wins, losses };
}

interface Detail {
  bench: number;
  optimal: number;
  topPlayerId: string | null;
  topPoints: number;
  projected: number;
  emptyStarters: number;
  inactiveStarters: number;
}

/** The current period's details from the live rows: bench, the optimal lineup, the top starter. */
function currentDetail(state: SimState, team: SimTeam, now: number): Detail {
  const rows = lineupRows(state, team, now);
  const starters = rows.filter((r) => r.slotKind === 'starter');
  const bench = round1(
    rows.filter((r) => r.slotKind === 'bench').reduce((s, r) => s + (r.points ?? 0), 0),
  );
  // `projection` is nullable on the contract since E1 — null means a server
  // has not projected the player. The simulator always has.
  const projected = round1(starters.reduce((s, r) => s + (r.projection ?? 0), 0));
  const top = starters
    .filter((r) => r.player && r.points !== null)
    .sort((a, b) => (b.points ?? 0) - (a.points ?? 0))[0];
  // The best lineup the roster could have started, greedy by points, dedicated slots first.
  const pool = rows
    .filter((r) => r.player && r.points !== null)
    .map((r) => ({ id: r.player!.id, pos: r.player!.positions[0]!, pts: r.points! }));
  const available = new Set(pool.map((p) => p.id));
  let optimal = 0;
  const slots = state.settings.roster.slots
    .map(({ slot, count }) => ({ def: slotDefs.get(slot), count }))
    .filter((s) => s.def?.kind === 'starter')
    .sort((a, b) => a.def!.eligible.length - b.def!.eligible.length);
  for (const { def, count } of slots)
    for (let i = 0; i < count; i++) {
      const best = pool
        .filter((p) => available.has(p.id) && def!.eligible.includes(p.pos))
        .sort((a, b) => b.pts - a.pts)[0];
      if (!best) continue;
      available.delete(best.id);
      optimal += best.pts;
    }
  return {
    bench,
    optimal: round1(optimal),
    topPlayerId: top?.player?.id ?? null,
    topPoints: top?.points ?? 0,
    projected,
    emptyStarters: starters.filter((r) => !r.player).length,
    inactiveStarters: starters.filter((r) => r.health === 'out' || r.health === 'bye').length,
  };
}

/**
 * What a picked-up player scored for the team in a finished period: his
 * points that week as one of its starters, from that week's box score
 * (`startedPoints`, B-527), as a real league counts them. Drawn apart from
 * his own points until 2026-10-26, so the card and his log disagreed.
 */
function pastPickupPoints(
  state: SimState,
  teamId: string,
  playerId: string,
  period: number,
): number {
  return startedPoints(state, teamId, playerId, period);
}

/** What a picked-up player is scoring for the team this period: his starter row, else nothing. */
function livePickupPoints(state: SimState, team: SimTeam, playerId: string, now: number): number {
  const row = lineupRows(state, team, now).find(
    (r) => r.slotKind === 'starter' && r.player?.id === playerId,
  );
  return row?.points ?? 0;
}

function periodWindow(state: SimState, period: number): { start: number; end: number } {
  const offset = (period - state.currentPeriod) * WEEK;
  return { start: state.periodStart + offset, end: state.periodEnd + offset };
}

/** The season as the awards engine reads it. */
export function seasonFacts(state: SimState, now: number): SeasonFacts {
  const teams = seated(state);
  const teamIds = new Set(teams.map((t) => t.id));
  const periods: PeriodFacts[] = [];
  const lastFinal = (() => {
    let last = 0;
    for (let p = 1; p <= state.currentPeriod; p++)
      if (periodStatus(state, p, now) === 'final' && state.schedule.some((m) => m.period === p))
        last = p;
    return last;
  })();

  for (let p = 1; p <= state.currentPeriod; p++) {
    const ms = state.schedule.filter((m) => m.period === p);
    if (!ms.length) continue;
    const status = periodStatus(state, p, now);
    const final = status === 'final';
    const past = p < state.currentPeriod;
    if (
      past &&
      !ms.every((m) => state.finals.has(`${p}:${m.home}`) && state.finals.has(`${p}:${m.away}`))
    )
      continue;
    const scoreOf = (teamId: string): number =>
      past
        ? (state.finals.get(`${p}:${teamId}`) ?? 0)
        : status === 'pre'
          ? 0
          : sideScore(
              state,
              state.teams.find((t) => t.id === teamId)!,
              now,
            ).score;
    const kind: PeriodFacts['kind'] = p <= state.regularSeasonEnd ? 'regular' : 'playoff';
    const matchups: MatchupFact[] = ms.map((m) => ({
      id: m.id,
      period: p,
      kind:
        p <= state.regularSeasonEnd ? 'regular' : m.id.startsWith('c') ? 'consolation' : 'playoff',
      homeTeamId: m.home,
      awayTeamId: m.away,
      homeScore: scoreOf(m.home),
      awayScore: scoreOf(m.away),
      final,
    }));
    const teamFacts: TeamPeriodFact[] = teams.map((team) => {
      const m = ms.find((x) => x.home === team.id || x.away === team.id);
      if (!m)
        return {
          teamId: team.id,
          period: p,
          played: false,
          opponentTeamId: null,
          score: 0,
          opponentScore: null,
          projected: 0,
          benchPoints: 0,
          optimalPoints: 0,
          topStarter: null,
          emptyStarters: 0,
          inactiveStarters: 0,
          result: null,
          opponentRecordBetter: false,
        };
      const opponentId = m.home === team.id ? m.away : m.home;
      const score = scoreOf(team.id);
      const opponentScore = scoreOf(opponentId);
      const detail: Detail = past
        ? (() => {
            const d = state.periodDetails.get(`${p}:${team.id}`);
            return d
              ? {
                  bench: d.bench,
                  optimal: d.optimal,
                  topPlayerId: d.topPlayerId,
                  topPoints: d.topPoints,
                  projected: d.projected,
                  emptyStarters: d.emptyStarters,
                  inactiveStarters: d.inactiveStarters,
                }
              : {
                  bench: 0,
                  optimal: score,
                  topPlayerId: null,
                  topPoints: 0,
                  projected: score,
                  emptyStarters: 0,
                  inactiveStarters: 0,
                };
          })()
        : currentDetail(state, team, now);
      const mine = recordBefore(state, team.id, p);
      const theirs = recordBefore(state, opponentId, p);
      return {
        teamId: team.id,
        period: p,
        played: status !== 'pre',
        opponentTeamId: opponentId,
        score,
        opponentScore,
        projected: detail.projected,
        benchPoints: detail.bench,
        optimalPoints: Math.max(detail.optimal, score),
        topStarter: detail.topPlayerId
          ? { playerId: detail.topPlayerId, points: detail.topPoints }
          : null,
        emptyStarters: detail.emptyStarters,
        inactiveStarters: detail.inactiveStarters,
        result: !final
          ? null
          : score > opponentScore
            ? 'win'
            : score < opponentScore
              ? 'loss'
              : 'tie',
        opponentRecordBetter:
          theirs.wins - theirs.losses > mine.wins - mine.losses ||
          (theirs.wins - theirs.losses === mine.wins - mine.losses && theirs.wins > mine.wins),
      };
    });
    // Pickups and trades in this period's window, with what the player did for the team.
    const { start, end } = periodWindow(state, p);
    const transactions: TransactionFact[] = [];
    for (const tx of state.waiverState.transactions) {
      if (tx.reversedBy || tx.at < start || tx.at >= end) continue;
      if (tx.kind !== 'add' && tx.kind !== 'claim' && tx.kind !== 'trade') continue;
      if (!teamIds.has(tx.teamId)) continue;
      const team = state.teams.find((t) => t.id === tx.teamId)!;
      for (const playerId of tx.added) {
        const thisPeriod = past
          ? pastPickupPoints(state, tx.teamId, playerId, p)
          : status === 'pre'
            ? 0
            : livePickupPoints(state, team, playerId, now);
        let since = thisPeriod;
        for (let q = p + 1; q <= lastFinal; q++)
          since += pastPickupPoints(state, tx.teamId, playerId, q);
        transactions.push({
          id: `${tx.id}:${playerId}`,
          period: p,
          teamId: tx.teamId,
          kind: tx.kind,
          playerId,
          pointsThisPeriod: round1(thisPeriod),
          pointsSince: round1(since),
        });
      }
    }
    periods.push({ period: p, kind, final, teams: teamFacts, matchups, transactions });
  }

  const standings = standingsView(state, now).rows.map((r, i) => ({
    teamId: r.team.id,
    // The simulator always ranks; the shape is nullable for a server that has
    // finalized nothing (E6), which the simulator never is.
    rank: r.rank ?? i + 1,
    wins: r.wins,
    losses: r.losses,
    ties: r.ties,
    pointsFor: r.pointsFor,
    pointsAgainst: r.pointsAgainst,
  }));

  const b = bracketView(state, now);
  const finalGame = b.rounds[b.rounds.length - 1]?.games[0] ?? null;
  const champion = b.champion?.id ?? null;
  let runnerUp: string | null = null;
  let finalLine: string | null = null;
  if (champion && finalGame && finalGame.top && finalGame.bottom) {
    const topWon = finalGame.top.team.id === champion;
    const loser = topWon ? finalGame.bottom : finalGame.top;
    runnerUp = loser.team.id;
    const winScore = topWon ? finalGame.topScore : finalGame.bottomScore;
    const loseScore = topWon ? finalGame.bottomScore : finalGame.topScore;
    finalLine =
      winScore !== null && loseScore !== null
        ? `Beat ${loser.team.name} ${winScore.toFixed(1)}–${loseScore.toFixed(1)} in the final.`
        : `Beat ${loser.team.name} in the final.`;
  }
  let lastPlace: string | null = null;
  if (b.status === 'final') {
    if (b.consolationKind === 'toilet-bowl' && b.consolation?.length) {
      const last = b.consolation[b.consolation.length - 1]!.games[0];
      lastPlace = last?.winnerTeamId ?? null;
    }
    lastPlace ??= standings[standings.length - 1]?.teamId ?? null;
  }

  return {
    leagueId: state.leagueId,
    season: state.season,
    teams: teams.map((t) => ({ teamId: t.id, name: t.name, ownerName: t.ownerName })),
    regularSeasonEnd: state.regularSeasonEnd,
    currentPeriod: state.currentPeriod,
    periods,
    standings,
    bracket: {
      final: b.status === 'final',
      championTeamId: champion,
      runnerUpTeamId: runnerUp,
      lastPlaceTeamId: lastPlace,
      finalLine,
      playoffTeamIds: b.seeds.map((s) => s.team.id),
    },
    history: state.history,
    playerName: (id) => state.players.get(id)?.name ?? 'somebody',
    trophyName: state.settings.identity.branding.trophyName,
  };
}

// ── Minting on the clock ──────────────────────────────────────────────────

function refsAt(state: SimState, now: number): Map<string, TeamRef> {
  return new Map(state.teams.map((t) => [t.id, teamRef(state, t, now)]));
}

function awardsCard(
  title: string,
  period: number | null,
  minted: readonly Minted[],
  refs: Map<string, TeamRef>,
): { body: string; card: ChatCard } {
  const items = minted.map((m) => {
    const w = m.winners[0]!;
    return {
      awardId: m.awardId,
      name: m.name,
      icon: m.icon,
      tone: m.tone,
      team: refs.get(w.teamId)!,
      valueLabel: formatValue(w.value, m.unit),
      line: m.line,
    };
  });
  const body = `${title}: ${items
    .map((i) => `${i.name}, ${i.team.name}${i.valueLabel ? ` (${i.valueLabel})` : ''}`)
    .join(' · ')}.`;
  return { body, card: { kind: 'awards', title, period, items } };
}

/**
 * New tiers for everyone since the last pass. Posts the three biggest as
 * cards and folds the rest into one line, so a week's end does not flood
 * the channel (a first week unlocks First Blood for half the league).
 */
function updateUnlocks(
  state: SimState,
  facts: SeasonFacts,
  period: number,
  at: number,
  post: boolean,
): void {
  const rules = rulesOf(state);
  if (!rules.milestones) return;
  const refs = refsAt(state, at);
  const fresh: Unlock[] = [];
  // WHAT THE SEASONS BEFORE THIS ONE REACHED is carried over, with no week,
  // and said nowhere, as a real league records it (`honors.ts`): Ken's League
  // starts with two seasons behind it.
  const before = factsThrough(facts, state.awards.minted, 0);
  for (const team of seated(state)) {
    const progress = milestoneProgress(facts, team.id, state.awards.minted, DEFAULT_CATALOG, rules);
    const carried = milestoneProgress(before.facts, team.id, before.minted, DEFAULT_CATALOG, rules);
    for (const p of progress) {
      let highest: Unlock | null = null;
      for (const tier of p.earnedTiers) {
        const key = `${team.id}:${p.id}:${tier}`;
        if (state.awards.unlocked.has(key)) continue;
        state.awards.unlocked.add(key);
        const old = carried.find((x) => x.id === p.id)?.earnedTiers.includes(tier) ?? false;
        const unlock: Unlock = {
          id: key,
          teamId: team.id,
          milestoneId: p.id,
          name: p.name,
          icon: p.icon,
          tone: p.tone,
          tier,
          line: p.earnedLine ?? p.blurb,
          at,
          period: old ? null : period,
        };
        state.awards.unlocks.push(unlock);
        if (!old) highest = unlock;
      }
      if (highest) fresh.push(highest);
    }
  }
  if (!post || !state.settings.fun.achievements.postToChat || !fresh.length) return;
  const ranked = [...fresh].sort((a, b) => tierRank(b.tier) - tierRank(a.tier));
  const cards = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  cards.forEach((u, i) => {
    const team = state.teams.find((t) => t.id === u.teamId)!;
    state.chat.push(
      systemMessage(
        state,
        at + i * 1000,
        `${team.ownerName} unlocked ${u.name} (${u.tier}). ${u.line}`,
        {
          kind: 'milestone',
          team: refs.get(u.teamId)!,
          milestoneId: u.milestoneId,
          name: u.name,
          icon: u.icon,
          tone: u.tone,
          tier: u.tier,
          line: u.line,
        },
      ),
    );
  });
  if (rest.length) {
    const named = rest
      .slice(0, 4)
      .map(
        (u) => `${state.teams.find((t) => t.id === u.teamId)?.ownerName ?? 'Someone'} (${u.name})`,
      )
      .join(', ');
    state.chat.push(
      systemMessage(
        state,
        at + cards.length * 1000,
        `${rest.length} more milestone${rest.length === 1 ? '' : 's'} unlocked: ${named}${rest.length > 4 ? ', and more' : ''}. See Awards.`,
      ),
    );
  }
}

function mintPeriod(state: SimState, period: number, finalAt: number, post: boolean): void {
  const rules = rulesOf(state);
  const facts = seasonFacts(state, finalAt);
  const results = periodAwards(facts, period, DEFAULT_CATALOG, rules);
  const at = finalAt + 2 * MIN;
  const minted = results.map((r) => mint(facts, r, at));
  state.awards.minted.push(...minted);
  if (post && state.settings.fun.leagueAwards.postToChat && minted.length) {
    const { body, card } = awardsCard(`Week ${period} awards`, period, minted, refsAt(state, at));
    state.chat.push(systemMessage(state, at, body, card));
  }
  // The recap reads the same facts and lands right after the awards card.
  if (post && state.settings.fun.leagueAwards.postToChat) {
    const recap = recapMessage(state, facts, period, at + 30_000);
    if (recap) state.chat.push(recap);
  }
  // Decided quietly while the switch is off, as a real league's is (`hats.ts`).
  decideHat(state, facts, period, at + 40_000, post && state.settings.fun.hat.enabled);
  updateUnlocks(state, facts, period, finalAt + 3 * MIN, post);
}

/**
 * The weekly hat (PLAN-2026-10-30-the-weekly-hat), as a real league's week
 * decides it (`weekHat`, `drawnHat`), with its card; a bot that picks it picks
 * at once, as a real league's does at its next look.
 */
function decideHat(
  state: SimState,
  facts: SeasonFacts,
  period: number,
  at: number,
  post: boolean,
): void {
  const decided = weekHat(facts, period);
  if (decided === null) return;
  const hat = drawnHat(state.leagueId, state.season, period);
  state.hat = { period, hat, wearers: decided.wearers, pickers: decided.pickers, pickedBy: null };
  const refs = refsAt(state, at);
  const wearers = decided.wearers.flatMap((id) => refs.get(id) ?? []);
  const pickers = decided.pickers.flatMap((id) => refs.get(id) ?? []);
  if (post && wearers.length > 0 && pickers.length > 0) {
    const names = (teams: TeamRef[]) =>
      teams.length <= 1
        ? (teams[0]?.name ?? 'Somebody')
        : `${teams
            .slice(0, -1)
            .map((t) => t.name)
            .join(', ')} and ${teams[teams.length - 1]!.name}`;
    const line =
      `${names(wearers)} ${wearers.length > 1 ? 'share' : 'gets'} Week ${period}’s hat for the fewest points, ` +
      `${decided.low.toFixed(1)}. ${names(pickers)} had the most, ${decided.high.toFixed(1)}, ` +
      `and ${pickers.length > 1 ? 'pick' : 'picks'} which.`;
    state.chat.push(
      systemMessage(state, at, line, { kind: 'hat', period, hat, wearers, pickers, line }),
    );
  }
  const bot = state.teams.find((t) => t.ai !== null && decided.pickers.includes(t.id));
  if (bot !== undefined && state.settings.fun.hat.enabled) {
    const pick =
      WEEKLY_HATS[
        Math.floor(rng(hashSeed(state.seed, `hat:${period}:${bot.id}`)).next() * WEEKLY_HATS.length)
      ]!;
    pickHat(state, bot.id, pick, at + MIN, post);
  }
}

/**
 * A pick of the week's hat by a team that may (PLAN-2026-10-30-the-weekly-hat
 * §4): the hat, and a line in the chat for each new one. False when the team
 * may not pick it, or there is no hat.
 */
export function pickHat(
  state: SimState,
  teamId: string,
  hat: WeeklyHat,
  at: number,
  post = true,
): boolean {
  const current = state.hat;
  if (!state.settings.fun.hat.enabled || current === null || !current.pickers.includes(teamId))
    return false;
  if (current.hat === hat && current.pickedBy !== null) return true;
  state.hat = { ...current, hat, pickedBy: teamId };
  if (post) {
    const name = (id: string) => state.teams.find((t) => t.id === id)?.name ?? 'a team';
    const wearers = current.wearers.map(name);
    const who =
      wearers.length <= 1
        ? (wearers[0] ?? 'the low scorer')
        : `${wearers.slice(0, -1).join(', ')} and ${wearers[wearers.length - 1]}`;
    state.chat.push(
      systemMessage(state, at, `${name(teamId)} put ${weeklyHatSaid(hat)} on ${who}.`),
    );
  }
  return true;
}

function mintSeason(state: SimState, facts: SeasonFacts, finalAt: number): void {
  const rules = rulesOf(state);
  const results = seasonAwards(facts, DEFAULT_CATALOG, rules);
  const at = finalAt + 4 * MIN;
  const minted = results.map((r) => mint(facts, r, at));
  state.awards.minted.push(...minted);
  const refs = refsAt(state, at);
  const champ = minted.find((m) => m.awardId === 'champion');
  if (state.settings.fun.leagueAwards.postToChat) {
    if (champ) {
      const team = refs.get(champ.winners[0]!.teamId)!;
      const runnerUp = facts.bracket.runnerUpTeamId
        ? (refs.get(facts.bracket.runnerUpTeamId) ?? null)
        : null;
      const trophyName = state.settings.identity.branding.trophyName;
      state.chat.push(
        systemMessage(
          state,
          at,
          `🏆 ${team.name} are your ${state.season} ${trophyName}. ${facts.bracket.finalLine ?? ''}`.trim(),
          { kind: 'champion', season: state.season, team, trophyName, line: champ.line, runnerUp },
        ),
      );
    }
    const rest = minted.filter((m) => m.awardId !== 'champion');
    if (rest.length) {
      const { body, card } = awardsCard(`${state.season} season awards`, null, rest, refs);
      state.chat.push(systemMessage(state, at + MIN, body, card));
    }
  }
  updateUnlocks(state, seasonFacts(state, at + 2 * MIN), state.currentPeriod, at + 2 * MIN, true);
}

/**
 * Mint what the clock has passed: the current period's weekly awards when
 * its last game ends, the season's when the championship period ends.
 * Idempotent; returns true when anything was minted.
 */
export function advanceAwards(state: SimState, now: number): boolean {
  if (state.status !== 'in-season' || state.proGames.length === 0) return false;
  const finalAt = periodFinalAt(state, state.currentPeriod);
  if (now < finalAt) return false;
  let changed = false;
  const key = `weekly:${state.currentPeriod}`;
  if (
    !state.awards.flags.has(key) &&
    state.schedule.some((m) => m.period === state.currentPeriod)
  ) {
    state.awards.flags.add(key);
    mintPeriod(state, state.currentPeriod, finalAt, true);
    changed = true;
  }
  const lastPlayoff = Math.max(state.regularSeasonEnd, ...state.settings.playoffs.periods);
  if (state.currentPeriod >= lastPlayoff && !state.awards.flags.has('season')) {
    const facts = seasonFacts(state, finalAt);
    if (facts.bracket.final) {
      state.awards.flags.add('season');
      mintSeason(state, facts, finalAt);
      changed = true;
    }
  }
  return changed;
}

/** At creation: every finished period's awards, silently, and last week's card in the channel. */
export function initAwards(state: SimState): void {
  if (state.status !== 'in-season') return;
  for (let p = 1; p < state.currentPeriod; p++) {
    if (!state.schedule.some((m) => m.period === p)) continue;
    state.awards.flags.add(`weekly:${p}`);
    mintPeriod(state, p, periodFinalAt(state, p), p === state.currentPeriod - 1);
  }
}

// ── Views ─────────────────────────────────────────────────────────────────

export function cardOf(r: AwardResult | Minted, refs: Map<string, TeamRef>): AwardCardView {
  const minted = 'id' in r ? r : null;
  return {
    id: minted ? minted.id : `live:${r.period ?? 'season'}:${r.awardId}`,
    awardId: r.awardId,
    name: r.name,
    blurb: r.blurb,
    icon: r.icon,
    tone: r.tone,
    scope: r.scope,
    period: r.period,
    asItStands: r.asItStands,
    winners: r.winners.map((w) => ({
      team: refs.get(w.teamId)!,
      value: w.value,
      valueLabel: formatValue(w.value, r.unit),
    })),
    standings: r.standings.map((s) => ({
      team: refs.get(s.teamId)!,
      valueLabel: formatValue(s.value, r.unit),
    })),
    line: r.line,
    at: minted ? iso(minted.at) : null,
  };
}

function unlockView(state: SimState, u: Unlock, refs: Map<string, TeamRef>): MilestoneUnlockView {
  return {
    id: u.id,
    team: refs.get(u.teamId)!,
    milestoneId: u.milestoneId,
    name: u.name,
    icon: u.icon,
    tone: u.tone,
    tier: u.tier,
    line: u.line,
    at: iso(u.at),
    earnedIn: earnedInLabel(u.period, u.period === null ? null : periodRef(state, u.period).label),
  };
}

/** The Awards screen: one period's weekly awards, the season race or results, recent unlocks. */
export function awardsView(state: SimState, now: number, period?: number): AwardsView {
  const facts = seasonFacts(state, now);
  const refs = refsAt(state, now);
  const rules = rulesOf(state);
  const current = state.currentPeriod;
  const status = state.status === 'in-season' ? periodStatus(state, current, now) : 'pre';
  const periods = facts.periods
    .filter((p) => p.final || (p.period === current && status !== 'pre'))
    .map((p) => p.period);
  const selected = period ?? periods[periods.length - 1] ?? current;
  const selectedFacts = facts.periods.find((p) => p.period === selected) ?? null;
  const mintedWeekly = state.awards.minted.filter(
    (m) => m.scope === 'period' && m.period === selected && !m.supersededBy,
  );
  const perPeriod = mintedWeekly.length
    ? mintedWeekly.map((m) => cardOf(m, refs))
    : selectedFacts && (selectedFacts.final || selected === current)
      ? periodAwards(facts, selected, DEFAULT_CATALOG, rules).map((r) => cardOf(r, refs))
      : [];
  const mintedSeason = state.awards.minted.filter((m) => m.scope === 'season' && !m.supersededBy);
  const seasonCards = mintedSeason.length
    ? mintedSeason.map((m) => cardOf(m, refs))
    : seasonAwards(facts, DEFAULT_CATALOG, rules).map((r) => cardOf(r, refs));
  const champ = mintedSeason.find((m) => m.awardId === 'champion') ?? null;
  const trophyName = state.settings.identity.branding.trophyName;
  return {
    league: leagueRef(state),
    period: periodRef(state, selected),
    status: selected === current ? status : 'final',
    periods,
    perPeriod,
    season: {
      status: mintedSeason.length ? 'final' : 'race',
      champion: champ
        ? {
            team: refs.get(champ.winners[0]!.teamId)!,
            trophyName,
            line: champ.line,
            season: state.season,
          }
        : null,
      awards: seasonCards,
    },
    recentUnlocks: [...state.awards.unlocks]
      .slice(-8)
      .reverse()
      .map((u) => unlockView(state, u, refs)),
    mine: perPeriod.filter((c) => c.winners.some((w) => w.team.isMine)),
    trophyName,
    updatedAt: iso(now),
  };
}

/**
 * A team's season wrapped (H9, PLAN-2026-10-18 §12.1): the same engine over
 * the same facts a real league's read uses. Null for a team not in the league.
 */
export function wrapView(state: SimState, teamId: string, now: number): SeasonWrapView | null {
  const refs = refsAt(state, now);
  const team = refs.get(teamId);
  if (!team) return null;
  const wrap = seasonWrap({
    facts: seasonFacts(state, now),
    teamId,
    mints: state.awards.minted,
    periodLabel: (p) => periodRef(state, p).label,
  });
  const refOf = (id: string | null) => (id === null ? null : (refs.get(id) ?? null));
  return {
    league: leagueRef(state),
    team,
    season: state.season,
    over: wrap.over,
    how: wrap.how,
    cards: wrap.cards.map((c) => ({
      id: c.id,
      title: c.title,
      value: c.value,
      line: c.line,
      team: refOf(c.teamId),
      matchupId: c.matchupId,
      awards: c.awards,
    })),
    share: wrap.share,
    updatedAt: iso(now),
  };
}

/**
 * The league's yearbook (H9, PLAN-2026-10-31-the-yearbook): the same engine
 * over the same facts a real league's read uses.
 */
export function yearbookView(state: SimState, now: number): YearbookView {
  const refs = refsAt(state, now);
  const book = leagueYearbook({
    facts: seasonFacts(state, now),
    mints: state.awards.minted,
    periodLabel: (p) => periodRef(state, p).label,
  });
  return {
    league: leagueRef(state),
    season: state.season,
    ...yearbookWith(book, (id) => refs.get(id) ?? null),
    updatedAt: iso(now),
  };
}

/** One manager's trophy room (AWARDS.md "The trophy room"). */
export function trophyRoomView(state: SimState, teamId: string, now: number): TrophyRoomView {
  const facts = seasonFacts(state, now);
  const rules = rulesOf(state);
  const room = trophyRoom(facts, teamId, state.awards.minted, DEFAULT_CATALOG, rules);
  const refs = refsAt(state, now);
  const team = state.teams.find((t) => t.id === teamId)!;
  const headline = careerLine(room);
  const when = new Map(
    state.awards.unlocks
      .filter((u) => u.teamId === teamId)
      .map((u) => [
        `${u.milestoneId}:${u.tier}`,
        earnedInLabel(u.period, u.period === null ? null : periodRef(state, u.period).label),
      ]),
  );
  const milestones: MilestoneView[] = room.milestones.map((a) => ({
    id: a.id,
    name: a.name,
    blurb: a.blurb,
    icon: a.icon,
    tone: a.tone,
    scope: a.scope,
    value: a.value,
    valueLabel: formatValue(a.value, a.unit),
    tier: a.tier,
    earnedLine: a.earnedLine,
    nextTier: a.nextTier,
    nextThreshold: a.nextThreshold,
    nextLabel:
      a.nextTier && a.nextThreshold !== null
        ? `${formatValue(a.value, a.unit)} of ${formatValue(a.nextThreshold, a.unit)} to ${a.nextTier}`
        : null,
    progress: a.progress,
    tiers: a.tiers.map((t) => ({
      ...t,
      earnedIn: t.earned ? (when.get(`${a.id}:${t.tier}`) ?? null) : null,
    })),
  }));
  const managers = seated(state)
    .map((t) => refs.get(t.id)!)
    .sort((a, b) => Number(b.isMine) - Number(a.isMine) || a.name.localeCompare(b.name));
  return {
    league: leagueRef(state),
    team: refs.get(teamId)!,
    owner: team.ownerName,
    isMine: team.isMine,
    headline,
    career: room.career,
    seasons: room.seasons,
    milestones,
    managers,
    shareText: `${team.ownerName}'s trophy room in ${state.leagueName}: ${headline}.`,
    trophyName: state.settings.identity.branding.trophyName,
    updatedAt: iso(now),
  };
}
